import React from "react";
import { MdLocationOn, MdBusiness } from "react-icons/md";
import jpmcLogo from "../assets/jpmc.jpeg";
import oliveLogo from "../assets/olive.jpeg";
import wiproLogo from "../assets/wipro.jpeg";
import sogetiLogo from "../assets/sogeti_logo.jpeg";

const WorkTimeline = () => {
  const experiences = [
    {
      id: 1,
      title: "Software/DevOps Engineer",
      company: "JPMorgan Chase & Co. (JPMC)",
      companyShort: "JPMC",
      logo: jpmcLogo,
      location: "Columbus, OH",
      period: "04-2024 - 06-2025",
      linkedinUrl: "https://www.linkedin.com/company/jpmorganchase/posts/?feedView=all",
      description: [
        "Delivered end-to-end full-stack Django and React application automating firewall rule configuration across hybrid environments",
        "Provisioned infrastructure with Terraform, implemented least privilege IAM policies, and automated CI/CD pipelines with Jenkins and Kubernetes (EKS), improving deployment velocity by 30%",
        "Deployed Kafka log aggregation with Splunk for real-time error tracking and debugging, reducing application downtime by 70%",
        "Built Grafana observability dashboards for Kubernetes cluster monitoring, tracing microservice issues, pod performance, and resource utilization",
        "Developed AWS ETL/ELT pipelines using Lambda, S3, and Athena for real-time firewall configuration and GraphQL APIs with Python, Scala, and RabbitMQ, reducing API latency by 60%",
        "Enhanced security pipeline with SAST and DAST tools, preventing sensitive data exposure and reducing production application flaws",
        "Tech Stack: Python, AWS, Terraform, Kubernetes, Docker, Jenkins, Grafana, SonarQube, Snyk, Django, React, MySQL, Oracle, S3, Lambda",
      ],
    },
    {
      id: 2,
      title: "DevOps Engineer",
      company: "Wipro",
      companyShort: "Wipro",
      logo: wiproLogo,
      location: "Remote",
      period: "04-2024 - 07-2024",
      linkedinUrl: "https://www.linkedin.com/company/wipro/posts/?feedView=all",
      description: [
        "Engineered and optimized GitLab CI/CD pipelines with multi-stage build processes, integrating automated testing and deployment workflows that accelerated software delivery by 50% and eliminated manual intervention bottlenecks",
        "Transformed application deployment velocity by containerizing applications with Docker and orchestrating scalable deployments through Kubernetes clusters, dramatically improving application reliability and scalability",
        "Tech Stack: AWS, Kubernetes, Docker, GitLab CI/CD, Prometheus, Grafana, Terraform, SonarQube, TypeScript, Python",
      ],
    },
    {
      id: 3,
      title: "Sr. Software Engineer",
      company: "Olive AI",
      companyShort: "Olive AI",
      logo: oliveLogo,
      location: "Columbus, OH",
      period: "03-2020 - 04-2023",
      linkedinUrl: "https://www.linkedin.com/company/olive-ai",
      description: [
        "Architected and developed cloud-native, full-stack applications using Java (Spring Boot) and Python (FastAPI) for backend microservices",
        "Built responsive frontend applications using React and TypeScript to create intuitive user interfaces for healthcare workflows",
        "Designed and implemented RESTful APIs and microservices architecture to automate complex healthcare revenue cycle operations",
        "Developed scalable automation solutions that processed high-volume transactions across multiple healthcare providers and insurance systems",
        "Integrated with third-party healthcare APIs and EMR systems to streamline data exchange and workflow automation",
        "Implemented cloud-native patterns including containerization, service mesh, and distributed systems for improved scalability and reliability",
        "Optimized revenue operations workflows by automating prior authorization, claims processing, and payment reconciliation processes",
        "Collaborated with product managers, clinical teams, and operations to translate business requirements into technical solutions",
        "Ensured HIPAA compliance and data security standards throughout the development lifecycle",
        "Tech Stack: Java, Python, TypeScript, Terraform, AWS Step Functions, EKS, Lambda, S3, DynamoDB, Glue, EC2, SQS, RDS",
      ],
    },
    {
      id: 4,
      title: "Software Development Engineer",
      company: "Sogeti",
      companyShort: "Sogeti",
      logo: sogetiLogo,
      location: "Cincinnati, OH",
      period: "01-2020 - 03-2020",
      linkedinUrl: "https://www.linkedin.com/company/sogeti",
      description: [
        "Migrated legacy Tkinter GUI application to Python/C++ scripting system, increasing operational efficiency and reducing errors by eliminating human intervention",
        "Improved software product delivery by automating build, test, and deployment workflows using Azure DevOps pipelines, ensuring consistent and reliable delivery across environments",
        "Implemented Precision Image Analysis (PIA) with OpenCV to detect defects and deformations in jet engine turbine blades",
        "Increased productivity by 80% through horizontal scaling and reduced errors by eliminating human intervention, improving overall operational efficiency",
        "Tech Stack: Python, C++, OpenCV, C#, Azure",
      ],
    },
  ];

  return (
    <div
      name="work-timeline"
      className="w-full bg-gradient-to-b from-gray-950 via-zinc-900 to-slate-950 text-white py-4 md:py-6 lg:py-8"
    >
      <div className="max-w-screen-lg mx-auto px-6 md:px-8">
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-1 w-12 bg-blue-500"></div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold tracking-tight">
              Work Experience
            </h3>
          </div>
        </div>

        <div className="relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-6 sm:left-8 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 bg-gray-700/50"></div>

          {/* Timeline Items */}
          <div className="space-y-0">
            {experiences.map((experience, index) => (
              <div
                key={experience.id}
                className={`relative flex flex-col md:flex-row items-start md:items-center ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                } ${index > 0 ? '-mt-4 md:-mt-6' : ''}`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-6 sm:left-8 md:left-1/2 transform md:-translate-x-1/2 z-20">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full border-2 border-gray-950 shadow-lg"></div>
                </div>

                {/* Content Card */}
                <div
                  className={`w-full md:w-[45%] ml-12 sm:ml-16 md:ml-0 relative ${
                    index === 0 ? 'z-30' : index === 1 ? 'z-20' : 'z-10'
                  } ${
                    index % 2 === 0 ? "md:mr-auto md:pr-4" : "md:ml-auto md:pl-4"
                  }`}
                >
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg p-2.5 sm:p-3 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500 hover:scale-[1.01]">
                    {/* Period Badge */}
                    <div className="text-[10px] sm:text-xs md:text-sm text-blue-400 font-semibold mb-1.5 tracking-wide uppercase">
                      {experience.period}
                    </div>

                    {/* Company Logo/Icon and Title */}
                    <div className="flex items-start gap-2 mb-1.5">
                      {/* Company Icon/Logo with LinkedIn Link */}
                      <a
                        href={experience.linkedinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                        aria-label={`Visit ${experience.company} on LinkedIn`}
                      >
                        {experience.logo ? (
                          <img 
                            src={experience.logo} 
                            alt={experience.company}
                            className="w-full h-full object-contain p-0.5 group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <span className="w-full h-full bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center text-white font-bold text-[10px] sm:text-xs md:text-sm group-hover:scale-110 transition-transform duration-300">
                            {experience.companyShort?.substring(0, 2) || experience.company.substring(0, 2)}
                          </span>
                        )}
                      </a>
                      
                      {/* Title */}
                      <div className="flex-1">
                        <h4 className="text-sm sm:text-base md:text-lg font-extrabold text-white mb-0.5 tracking-tight">
                          {experience.title}
                        </h4>
                        <div className="flex items-center gap-1">
                          <MdBusiness className="text-blue-400" size={12} />
                          <span className="text-[10px] sm:text-xs md:text-sm font-semibold tracking-wide text-gray-200">
                            {experience.company}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-1 mb-2 text-gray-200">
                      <MdLocationOn className="text-blue-400" size={12} />
                      <span className="text-[10px] sm:text-xs font-light tracking-wide">{experience.location}</span>
                    </div>

                    {/* Tech Stack Only */}
                    {experience.description.map((item, idx) => {
                      // Only show tech stack items
                      const isTechStack = item.startsWith("Tech Stack:");
                      if (isTechStack) {
                        const techs = item.replace("Tech Stack:", "").trim().split(", ");
                        return (
                          <div key={idx} className="mt-2">
                            <div className="text-[10px] sm:text-xs font-semibold text-blue-400 mb-1.5 tracking-wide uppercase">
                              Tech Stack
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-1.5">
                              {techs.map((tech, techIdx) => (
                                <span
                                  key={techIdx}
                                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] xs:text-[10px] sm:text-xs font-medium bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-md text-blue-300 hover:from-blue-600/30 hover:to-cyan-600/30 hover:border-blue-500/50 hover:scale-105 transition-all duration-300 cursor-default"
                                >
                                  {tech.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkTimeline;

